import { useAtomValue } from 'jotai';
import { useRef } from 'react';
import { ModalManager, ModalManagerProps } from '@placer-ui/components';
import { FormSectionType } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { useTableConfirmationModals } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/use-table-confirmation-modals';
import { useOpenSupportRequest } from 'features/my-zone/support-requests/components/support-requests-modals/support-modal-manager';
import { getFeatureFlags } from 'core/flow-control';
import {
    isUpdateTagEnabledStore,
    statusMapStore,
    supportTableTagIdStore,
    tempDataTableStore,
} from 'features/my-zone/support-requests/store/property-store';
import { useResetSupportState } from 'features/my-zone/support-requests/hooks/use-reset-support-state';
import { useSendSupportSplunkEvent } from 'features/my-zone/support-requests/hooks/use-send-support-splunk-event';
import { useTraceSupportQueryParam } from 'features/my-zone/support-requests/hooks/use-trace-support-query-param';

export const useSupportModalProps = () => {
    const supportTableTagId = useAtomValue(supportTableTagIdStore);
    const isUpdateTagEnabled = useAtomValue(isUpdateTagEnabledStore);
    const statusMap = useAtomValue(statusMapStore);
    const { closeSupportRequest } = useOpenSupportRequest();
    const { enable_poi_upload_save_as_tag, enable_limit_add_to_tag_ff } = getFeatureFlags();
    const sendSplunkEvent = useSendSupportSplunkEvent();
    const { clearSupportRequestInQuery } = useTraceSupportQueryParam();
    const { resetState } = useResetSupportState();
    const tempDataTable = useAtomValue(tempDataTableStore);

    const disableTagModals = enable_limit_add_to_tag_ff && (tempDataTable?.length || 0) > 2000;

    const modalManager = ModalManager.getInstance();

    const forceSubmit = useRef({
        'save-as-tag': false,
        'update-tag': false,
    });

    const handleSkipModal = (type: string) => {
        modalManager.removeModal(type);
        forceSubmit.current = { ...forceSubmit.current, [type]: true };
        handleCloseDataValidation();
    };

    const { showSaveAsTagModal, showUpdateTagModal } = useTableConfirmationModals({
        onSkipModal: handleSkipModal,
    });

    const handleCloseDataValidation = () => {
        const shouldShowSaveTagModal =
            enable_poi_upload_save_as_tag &&
            !forceSubmit.current['save-as-tag'] &&
            !supportTableTagId &&
            !disableTagModals;

        const shouldShowUpdateTagModal =
            enable_poi_upload_save_as_tag &&
            !forceSubmit.current['update-tag'] &&
            isUpdateTagEnabled &&
            !disableTagModals;

        if (shouldShowSaveTagModal) {
            return showSaveAsTagModal(undefined);
        }

        if (shouldShowUpdateTagModal) {
            return showUpdateTagModal(undefined);
        }

        clearSupportRequestInQuery();
        resetState();
        sendSplunkEvent({
            action: 'button-click',
            value: 'close property',
            report_config: statusMap,
        });
        closeSupportRequest();
    };

    return {
        getSectionConfig: (sectionType: FormSectionType) => {
            const modalPropsConfig: Record<FormSectionType, Partial<ModalManagerProps>> = {
                'request-properties': { fullSize: false },
                'match-file-data': { fullSize: false },
                'data-validation': {
                    fullSize: true,
                    onOpenChange: (open: boolean) => {
                        if (!open) {
                            handleCloseDataValidation();
                            return true;
                        }
                    },
                },
                'coverage-check': { fullSize: false },
                submit: { fullSize: false },
            };

            return modalPropsConfig[sectionType];
        },
    };
};
